import {
  OITAVOS_DO_TANQUE,
  type RegistrarVistoria,
  type SalvarChecklist,
  anguloDeFotoSchema,
  gravidadeDeAvariaSchema,
  itemDeChecklistSchema,
  respostaDeChecklistSchema,
  tipoDeAvariaSchema,
  tipoDeVistoriaSchema,
} from '@locaveri/shared'
import {
  type QueryClient,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet, apiPost, apiRequest } from '@/lib/api'
import { ROTAS_DE_ARQUIVOS, ROTAS_DE_VISTORIAS } from '@/lib/endpoints'

/**
 * Vistorias — checklist do tenant, registro, listagem, comparativo e o upload
 * assinado das fotos e da assinatura.
 *
 * ---------------------------------------------------------------------------
 * O QUE VEM DE ONDE
 * ---------------------------------------------------------------------------
 * O modelo de LEITURA é declarado aqui (Zod local), espelhando os DTOs de
 * saída de `apps/api/src/vistorias/vistorias.dto.ts` — o padrão de
 * `features/precos/api.ts`. O contrato de ENTRADA é o de `@locaveri/shared`
 * (`registrarVistoriaSchema`, `salvarChecklistSchema`): o mesmo formulário que
 * o app do pátio valida é o que a API recebe, e uma segunda cópia aqui seria o
 * dia em que o painel aprova uma vistoria que a API recusa.
 *
 * Ao contrário dos formulários de dinheiro, os corpos de vistoria PODEM viajar
 * como `validado.data`: estes schemas só coagem string para número e aplicam
 * defaults — nada vira `bigint` nem `Date`, os dois tipos que `JSON.stringify`
 * não serializa. Por isso as mutações são tipadas com `z.infer` do shared.
 *
 * ---------------------------------------------------------------------------
 * O UPLOAD NÃO PASSA PELO `apiRequest`
 * ---------------------------------------------------------------------------
 * O binário nunca toca a API: ela só ASSINA a URL. O `PUT` no storage é um
 * `fetch` cru de propósito — o MinIO/S3 não fala o envelope de erro da API,
 * não quer Bearer (mandar o access token para outro host seria vazá-lo) e a
 * assinatura exige EXATAMENTE os cabeçalhos devolvidos, nem um a mais.
 *
 * ---------------------------------------------------------------------------
 * REGISTRAR VISTORIA MEXE EM MEIO PAINEL
 * ---------------------------------------------------------------------------
 * A SAÍDA move a reserva para EM_ANDAMENTO e o veículo para EM_LOCACAO; o
 * RETORNO move para DEVOLUCAO_PENDENTE e LIMPEZA — tudo na mesma transação da
 * API. As chaves de cache de reservas, frota e disponibilidade vivem em
 * features que estão sendo construídas EM PARALELO, e adivinhar as strings
 * aqui criaria uma dependência invisível que quebra sem erro de compilação.
 * Como registrar/anular vistoria é gesto raro (uma ou duas vezes por locação),
 * a invalidação derruba o cache INTEIRO — caro uma vez, correto sempre.
 */

// ---------------------------------------------------------------------------
// Leitura — espelhos dos DTOs de saída da API
// ---------------------------------------------------------------------------

/** O checklist do tenant. `itens` vazio é "não configurado", não erro. */
export const checklistDeVistoriaSchema = z.object({
  itens: z.array(itemDeChecklistSchema),
})

export type ChecklistDeVistoria = z.infer<typeof checklistDeVistoriaSchema>

export const fotoDeVistoriaLidaSchema = z.object({
  id: z.uuid(),
  angulo: anguloDeFotoSchema,
  /**
   * A CHAVE do arquivo no storage (módulo `arquivos`) — é ela que
   * `useUrlDeLeitura` troca por uma URL assinada para exibir a foto.
   */
  arquivoChave: z.string(),
})

export type FotoDeVistoriaLida = z.infer<typeof fotoDeVistoriaLidaSchema>

export const avariaLidaSchema = z.object({
  id: z.uuid(),
  tipo: tipoDeAvariaSchema,
  gravidade: gravidadeDeAvariaSchema,
  posicaoXEmMilesimos: z.number().int(),
  posicaoYEmMilesimos: z.number().int(),
  descricao: z.string(),
  fotoArquivoChave: z.string().nullable(),
})

export type AvariaLida = z.infer<typeof avariaLidaSchema>

/**
 * Uma vistoria como a API a devolve — inclusive as ANULADAS, marcadas com
 * motivo em vez de filtradas: a listagem é a trilha da disputa, e "por que
 * houve duas saídas nesta reserva" é o que ela existe para responder.
 */
export const vistoriaLidaSchema = z.object({
  id: z.uuid(),
  reservationId: z.uuid(),
  /** O trecho de alocação vistoriado. `null` quando não havia alocação ativa. */
  allocationId: z.uuid().nullable(),
  vehicleId: z.uuid(),

  tipo: tipoDeVistoriaSchema,
  hodometro: z.number().int().nonnegative(),
  combustivelEmOitavos: z.number().int().min(0).max(OITAVOS_DO_TANQUE),

  respostas: z.array(respostaDeChecklistSchema),
  fotos: z.array(fotoDeVistoriaLidaSchema),
  avarias: z.array(avariaLidaSchema),

  assinaturaArquivoChave: z.string(),
  observacoes: z.string().nullable(),

  anulada: z.boolean(),
  anuladaEm: z.string().nullable(),
  motivoDaAnulacao: z.string().nullable(),

  realizadaPorId: z.uuid().nullable(),
  criadoEm: z.string(),
})

export type Vistoria = z.infer<typeof vistoriaLidaSchema>

/** Sem paginação, como na API: são no máximo um punhado por reserva. */
export const listaDeVistoriasSchema = z.object({ itens: z.array(vistoriaLidaSchema) })

export type ListaDeVistorias = z.infer<typeof listaDeVistoriasSchema>

/**
 * Saída × retorno com as avarias NOVAS já calculadas pela API (a mesma função
 * determinística de `@locaveri/shared`). O número nasce no servidor porque é
 * sobre ele que o fechamento cobra — o painel PREFERE este endpoint a rodar
 * `avariasNovas` localmente, para nunca exibir uma conta diferente da que vai
 * ser faturada. Campos `null` enquanto a vistoria correspondente não existe.
 */
export const comparativoDeVistoriasSchema = z.object({
  saida: vistoriaLidaSchema.nullable(),
  retorno: vistoriaLidaSchema.nullable(),
  avariasNovas: z.array(avariaLidaSchema),
})

export type ComparativoDeVistorias = z.infer<typeof comparativoDeVistoriasSchema>

/** O que `POST /arquivos/*` devolve — espelho de `arquivos.dto.ts`. */
const uploadAssinadoSchema = z.object({
  /** `PUT` aqui, com exatamente os cabeçalhos abaixo. */
  urlDeUpload: z.string(),
  cabecalhos: z.record(z.string(), z.string()),
  /** O que se grava/exibe depois que o `PUT` responder 200. */
  urlDoArquivo: z.string(),
  chave: z.string(),
  expiraEmSegundos: z.number().int().positive(),
})

// ---------------------------------------------------------------------------
// Chaves de cache
// ---------------------------------------------------------------------------

export const CHAVE_DO_CHECKLIST = 'checklist-de-vistoria'
export const CHAVE_DE_VISTORIAS = 'vistorias-da-reserva'
export const CHAVE_DO_COMPARATIVO = 'comparativo-de-vistorias'

/**
 * Ver o cabeçalho do arquivo: registrar/anular vistoria muda reserva, veículo
 * e disponibilidade, cujas chaves moram em features construídas em paralelo.
 * Sem filtro, TODAS as queries ficam stale e as ativas refazem — o preço de
 * uma tela inteira recarregada, pago numa ação que acontece duas vezes por
 * locação.
 */
function invalidarTudo(queryClient: QueryClient): void {
  void queryClient.invalidateQueries()
}

// ---------------------------------------------------------------------------
// Checklist
// ---------------------------------------------------------------------------

export function useChecklist(): UseQueryResult<ChecklistDeVistoria> {
  return useQuery({
    queryKey: [CHAVE_DO_CHECKLIST],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_VISTORIAS.checklist, checklistDeVistoriaSchema, { signal }),
  })
}

/**
 * Salva o checklist inteiro (`PUT`, substituição): a unicidade das chaves é
 * propriedade do CONJUNTO, como nas tarifas. Só o próprio checklist sai do
 * cache — vistorias já gravadas são imutáveis e não mudam com ele.
 */
export function useSalvarChecklist(): UseMutationResult<
  ChecklistDeVistoria,
  Error,
  SalvarChecklist
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: SalvarChecklist) =>
      apiRequest(ROTAS_DE_VISTORIAS.checklist, {
        schema: checklistDeVistoriaSchema,
        method: 'PUT',
        body: dados,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DO_CHECKLIST] })
    },
  })
}

// ---------------------------------------------------------------------------
// Vistorias da reserva
// ---------------------------------------------------------------------------

export function useVistoriasDaReserva(reservaId: string | null): UseQueryResult<ListaDeVistorias> {
  return useQuery({
    queryKey: [CHAVE_DE_VISTORIAS, reservaId],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_VISTORIAS.daReserva(reservaId ?? ''), listaDeVistoriasSchema, { signal }),
    // Sem reserva na rota não existe pergunta a fazer.
    enabled: reservaId !== null,
  })
}

export function useComparativo(reservaId: string | null): UseQueryResult<ComparativoDeVistorias> {
  return useQuery({
    queryKey: [CHAVE_DO_COMPARATIVO, reservaId],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_VISTORIAS.comparativo(reservaId ?? ''), comparativoDeVistoriasSchema, {
        signal,
      }),
    enabled: reservaId !== null,
  })
}

/**
 * Registra a vistoria de saída ou de retorno.
 *
 * O corpo é `validado.data` de `registrarVistoriaSchema` — ver o cabeçalho: é
 * o único lugar do painel onde a saída do schema compartilhado é JSON-safe.
 * O 409 (já existe vistoria válida do tipo) e o 422 (status da reserva,
 * checklist obrigatório sem resposta) chegam como `ApiError` com a mensagem da
 * API — a tela os mostra em toast, sem traduzir de novo.
 */
export function useRegistrarVistoria(
  reservaId: string | null,
): UseMutationResult<Vistoria, Error, RegistrarVistoria> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: RegistrarVistoria) =>
      apiPost(ROTAS_DE_VISTORIAS.daReserva(reservaId ?? ''), vistoriaLidaSchema, dados),
    onSuccess: () => {
      invalidarTudo(queryClient)
    },
  })
}

export interface AnulacaoDeVistoria {
  readonly vistoriaId: string
  /** Mínimo de 5 caracteres — vai para a trilha da disputa. */
  readonly motivo: string
}

/**
 * Anula uma vistoria (POST com motivo — ela FICA na lista, marcada). Depois de
 * a reserva ser finalizada a API recusa com 409; o botão da tela abre o
 * diálogo mesmo assim, porque quem decide é o servidor, não um status cacheado.
 */
export function useAnularVistoria(
  reservaId: string | null,
): UseMutationResult<Vistoria, Error, AnulacaoDeVistoria> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ vistoriaId, motivo }: AnulacaoDeVistoria) =>
      apiPost(ROTAS_DE_VISTORIAS.anular(reservaId ?? '', vistoriaId), vistoriaLidaSchema, {
        motivo,
      }),
    onSuccess: () => {
      invalidarTudo(queryClient)
    },
  })
}

// ---------------------------------------------------------------------------
// Upload assinado
// ---------------------------------------------------------------------------

/**
 * Cada finalidade tem ROTA própria porque tem permissão própria — a finalidade
 * nunca vai no corpo (seria reabrir a brecha que as rotas fecham).
 */
export const FINALIDADES_DE_UPLOAD = ['FOTO_DE_VISTORIA', 'ASSINATURA_DE_VISTORIA'] as const

export type FinalidadeDeUpload = (typeof FINALIDADES_DE_UPLOAD)[number]

const ROTA_DA_FINALIDADE: Readonly<Record<FinalidadeDeUpload, string>> = {
  FOTO_DE_VISTORIA: ROTAS_DE_ARQUIVOS.fotoDeVistoria,
  ASSINATURA_DE_VISTORIA: ROTAS_DE_ARQUIVOS.assinaturaDeVistoria,
}

export interface UploadConcluido {
  /**
   * A CHAVE do storage (`tenants/<tenant>/<pasta>/<uuid>.<ext>`) — é ela que a
   * vistoria grava (`arquivoChave`/`assinaturaArquivoChave`) e que a URL de
   * leitura assinada troca depois. Um UUID solto, sem pasta e sem extensão,
   * seria um arquivo que ninguém reabre.
   */
  readonly chave: string
  /** URL pública, para exibir a miniatura sem baixar de novo do disco. */
  readonly urlDoArquivo: string
}

/**
 * O `PUT` do binário, direto no storage. `fetch` cru de propósito (ver o
 * cabeçalho do arquivo) — e os cabeçalhos são EXATAMENTE os assinados:
 * acrescentar um `Authorization` aqui invalidaria a assinatura.
 */
async function enviarParaOStorage(
  urlDeUpload: string,
  cabecalhos: Readonly<Record<string, string>>,
  arquivo: Blob,
): Promise<void> {
  let resposta: Response

  try {
    resposta = await fetch(urlDeUpload, { method: 'PUT', headers: cabecalhos, body: arquivo })
  } catch {
    throw new Error('Não foi possível falar com o armazenamento de arquivos. Confira a conexão.')
  }

  if (!resposta.ok) {
    throw new Error(
      `O armazenamento recusou o arquivo (HTTP ${String(resposta.status)}). Tente de novo.`,
    )
  }
}

/**
 * Upload em dois passos: a API assina (`POST` autenticado, que é onde o teto de
 * tamanho e o tipo MIME são validados por finalidade) e o navegador faz o
 * `PUT` direto no storage. O que volta é a CHAVE pronta para o formulário —
 * quem chama nunca vê URL assinada de upload.
 */
export function useAssinarUpload(
  finalidade: FinalidadeDeUpload,
): UseMutationResult<UploadConcluido, Error, Blob> {
  return useMutation({
    mutationFn: async (arquivo: Blob) => {
      const assinado = await apiPost(ROTA_DA_FINALIDADE[finalidade], uploadAssinadoSchema, {
        tipoMime: arquivo.type,
        tamanhoEmBytes: arquivo.size,
      })

      await enviarParaOStorage(assinado.urlDeUpload, assinado.cabecalhos, arquivo)

      return { chave: assinado.chave, urlDoArquivo: assinado.urlDoArquivo }
    },
  })
}
